import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { fmtEur, FOOD_SERVICE_LABELS, GROUP_LABELS, guestCounts, TORTA_SERVICE_PP, TORTA_KG_PRICE, TORTA_GR_PP } from "@/lib/quote";

export const FoodStep = ({ data, update }) => {
  const food = data.food || {};
  const counts = useMemo(() => guestCounts(data.guests), [data.guests]);
  const activeGroups = useMemo(() => Object.entries(counts).filter(([, n]) => n > 0), [counts]);
  const tot = counts.adulti + counts.ragazzi + counts.bambini;
  const setFood = (patch) => update("food", null, patch);
  const setGroup = (g, patch) =>
    setFood({ groups: { ...(food.groups || {}), [g]: { ...(food.groups?.[g] || { service: "buffet", budget_pp: 0 }), ...patch } } });

  const kg = Math.round(tot * (TORTA_GR_PP / 1000) * 100) / 100;
  const torte = [
    { id: "nessuna", label: "Nessuna torta", sub: "non serve la torta", extra: "incluso" },
    { id: "esterna", label: "Torta da pasticceria esterna", sub: `costo servizio torta ${fmtEur(TORTA_SERVICE_PP)} a persona`, extra: `+${fmtEur(TORTA_SERVICE_PP * tot)}` },
    { id: "bliss", label: "Torta fornita da Bliss", sub: `da ${TORTA_KG_PRICE} €/kg — per ${tot} ospiti calcoliamo ~${kg} kg (${TORTA_GR_PP} g a persona). Prezzo indicativo, può variare`, extra: `~${fmtEur(kg * TORTA_KG_PRICE)}` },
  ];

  return (
    <div className="bg-white rounded-2xl border p-6 sm:p-8 shadow-sm space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#2C1A11]">Food</h2>
        <p className="text-sm text-[#8a7a6d]">Per ogni gruppo di ospiti scegli il tipo di servizio e il budget dedicato: prepareremo noi la proposta di menù</p>
      </div>

      {activeGroups.map(([g, n]) => {
        const cfg = food.groups?.[g] || { service: "buffet", budget_pp: 0 };
        const b = Number(cfg.budget_pp) || 0;
        return (
          <div key={g} data-testid={`food-group-${g}`} className="rounded-xl border border-[#e5ddd0] bg-[#FAF7F2] p-4 space-y-3">
            <div className="font-semibold text-sm text-[#2C1A11]">{GROUP_LABELS[g]} <span className="text-[#8a7a6d] font-normal">({n} {n === 1 ? "ospite" : "ospiti"})</span></div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-2">
                {[["buffet", "Buffet"], ["tavola", "Seduti a tavola"]].map(([id, label]) => (
                  <button key={id} data-testid={`food-${g}-service-${id}`} onClick={() => setGroup(g, { service: id })}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${cfg.service === id ? "bg-[#C85A32] text-white border-[#C85A32]" : "bg-white text-[#2C1A11] hover:border-[#C85A32]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#8a7a6d]">Budget €/persona</span>
                <Input data-testid={`food-${g}-budget-input`} type="number" min="0" className="w-24 bg-white" value={cfg.budget_pp || ""} placeholder="Es. 20"
                  onChange={(e) => setGroup(g, { budget_pp: Math.max(0, Number(e.target.value)) })} />
              </div>
              {b > 0 && <span className="font-mono-num font-bold text-sm text-[#C85A32] ml-auto">{fmtEur(b * n)}</span>}
            </div>
            {b > 0 && <div className="text-xs text-[#4A6B5D]">Servizio {FOOD_SERVICE_LABELS[cfg.service]} · il Bliss Café comporrà il menù in base al budget indicato</div>}
          </div>
        );
      })}

      <div>
        <div className="text-sm font-semibold text-[#2C1A11] mb-2">Preferenze food (opzionale)</div>
        <textarea data-testid="food-note-input" rows={3}
          className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
          placeholder="Es. niente fritti, preferiamo pesce, cucina tradizionale, piatti per bambini semplici…"
          value={food.note || ""} onChange={(e) => setFood({ note: e.target.value })} />
        <div className="text-xs text-[#8a7a6d] mt-1">Raccontaci i tuoi gusti: ci aiuta a proporti il menù giusto al primo colpo</div>
      </div>

      <div>
        <div className="text-sm font-semibold text-[#2C1A11] mb-2">Torta</div>
        <div className="space-y-2">
          {torte.map((t) => (
            <button key={t.id} data-testid={`torta-option-${t.id}`} onClick={() => setFood({ torta: t.id })}
              className={`w-full flex items-center justify-between gap-3 rounded-xl border p-4 text-left transition-colors ${(food.torta || "nessuna") === t.id ? "border-[#C85A32] ring-1 ring-[#C85A32] bg-[#C85A32]/5" : "bg-white hover:border-[#C85A32]/50"}`}>
              <span>
                <span className="font-semibold text-sm text-[#2C1A11] block">{t.label}</span>
                <span className="text-xs text-[#8a7a6d]">{t.sub}</span>
              </span>
              <span className={`font-mono-num font-bold text-sm shrink-0 ${t.id === "nessuna" ? "text-emerald-600" : "text-[#C85A32]"}`}>{t.extra}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
